import {
  MEMBERSHIP_STATES,
  type MembershipState,
} from "@/lib/constants/membership-states";
import type { AdminMembershipDetail } from "@/types/admin-membership";

const VALID_STATES = new Set<string>(MEMBERSHIP_STATES);

function readString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function readNullableString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function readNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/**
 * `null` when the gateway reports no membership (its not_configured stub) or a
 * value outside the canonical vocabulary. This used to fall back to "active",
 * which made the Membership tab claim an active membership for accounts that
 * had never purchased anything.
 */
function readMembershipState(value: unknown): MembershipState | null {
  if (typeof value === "string" && VALID_STATES.has(value)) {
    return value as MembershipState;
  }
  return null;
}

function readPath(
  value: unknown,
): AdminMembershipDetail["path"] {
  if (
    value === "trial" ||
    value === "paid_trial" ||
    value === "direct" ||
    value === "comp"
  ) {
    return value;
  }
  return "direct";
}

function readFutureBilling(
  value: unknown,
): AdminMembershipDetail["futureBilling"] | undefined {
  if (value === "annual" || value === "monthly" || value === "none") {
    return value;
  }
  return undefined;
}

function readCurrentBilling(
  value: unknown,
): AdminMembershipDetail["currentBilling"] {
  if (value === "trial" || value === "annual" || value === "monthly") {
    return value;
  }
  return null;
}

function readNullableBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

/**
 * Map backend membership payloads (camelCase or snake_case, optional fields)
 * into the dashboard `AdminMembershipDetail` contract.
 */
export function normalizeAdminMembershipDetail(
  raw: Record<string, unknown>,
  fallbackUserId: string,
): AdminMembershipDetail {
  const pathRaw = raw.path ?? raw.purchasePath ?? raw.purchase_path;
  const futureBillingRaw =
    raw.futureBilling ?? raw.future_billing ?? raw.futureBillingPreference;
  // W65-4: the gateway has no `conversionAt`. It sends `startedAt` — "the paid
  // lifecycle began" — which for a trial member is the conversion.
  const startedAt = readNullableString(raw.startedAt ?? raw.started_at);

  return {
    membershipId:
      readString(raw.membershipId) ??
      readString(raw.membership_id) ??
      "",
    userId:
      readString(raw.userId) ??
      readString(raw.user_id) ??
      fallbackUserId,
    state: readMembershipState(raw.state),
    path: readPath(pathRaw),
    futureBilling: readFutureBilling(futureBillingRaw),
    trialStartedAt: readNullableString(
      raw.trialStartedAt ?? raw.trial_started_at,
    ),
    trialEndsAt: readNullableString(raw.trialEndsAt ?? raw.trial_ends_at),
    conversionAt:
      readNullableString(raw.conversionAt ?? raw.conversion_at) ??
      (pathRaw === "trial" ? startedAt : null),
    commitmentStartedAt: startedAt,
    currentBilling: readCurrentBilling(raw.currentBilling ?? raw.current_billing),
    inCommitment: readNullableBoolean(raw.inCommitment ?? raw.in_commitment),
    commitmentEndsAt: readNullableString(
      raw.commitmentEndsAt ?? raw.commitment_ends_at,
    ),
    pausedUntil: readNullableString(raw.pausedUntil ?? raw.paused_until),
    cancellationScheduledAt: readNullableString(
      raw.cancellationScheduledAt ?? raw.cancellation_scheduled_at,
    ),
    paymentMethodLast4: readNullableString(
      raw.paymentMethodLast4 ?? raw.payment_method_last4,
    ),
    paymentMethodBrand: readNullableString(
      raw.paymentMethodBrand ?? raw.payment_method_brand,
    ),
    amountMinor: readNumber(raw.amountMinor ?? raw.amount_minor),
    currency: readString(raw.currency) ?? "USD",
    nextBillingAt: readNullableString(
      raw.nextBillingAt ??
        raw.next_billing_at ??
        raw.renewalDate ??
        raw.renewal_date,
    ),
    failedPaymentCount: readNumber(
      raw.failedPaymentCount ?? raw.failed_payment_count,
    ),
    pastDueSinceAt: readNullableString(
      raw.pastDueSinceAt ?? raw.past_due_since_at,
    ),
    gracePeriodEndsAt: readNullableString(
      raw.gracePeriodEndsAt ?? raw.grace_period_ends_at,
    ),
    returnWindowOpen:
      (raw.returnWindowOpen ?? raw.return_window_open) === true,
    returnWindowEndsAt: readNullableString(
      raw.returnWindowEndsAt ?? raw.return_window_ends_at,
    ),
    pauseCycles12mo: readNumber(
      raw.pauseCycles12mo ?? raw.pause_cycles_12mo,
    ),
    churnRisk: (raw.churnRisk ?? raw.churn_risk) === true,
  };
}
